import { BackIcon } from "@/components/icons/back-icon";
import { ChevronDownIcon } from "@/components/icons/chevron-down-icon";
import { NewChatIcon } from "@/components/icons/new-chat-icon";
import { TextButton } from "@/components/ui/buttons";
import { MixpanelEvents } from "@/constants/mixpanel-events";
import {
  chatMessagesAtom,
  chatOriginAtom,
  chatSessionIdAtom,
  mealPickerGreetingPendingAtom,
  pendingChatContextAtom,
  pendingChatImageAtom,
} from "@/stores/chat.store";
import { clearChatWelcomePending } from "@/utils/chat-welcome-flag";
import { cn } from "@/utils/cn";
import { trackMixpanelEvent } from "@/utils/mixpanel";
import { textVariants } from "@/utils/text-variants";
import { useIonActionSheet } from "@ionic/react";
import { useSetAtom } from "jotai";
import { memo } from "react";

interface ChatHeaderProps {
  hasStarted: boolean;
  onBack: () => void;
  onNewChat?: () => boolean | Promise<boolean>;
  /**
   * Shows a help ("?") button to the right of the new-chat pencil once
   * `showActions` is true. Used by the meal-picker greeting (Figma node
   * 273:1163), which has no history to clear yet but still wants a help
   * action. Rendered invisibly (kept in the DOM for layout balance) while
   * `showActions` is false.
   */
  onHelp?: () => void;
  /**
   * Force the "NutriCoach" title to render even when `hasStarted` is false.
   * Used for the post-onboarding welcome state, which shows the title but
   * still hides the new-chat pencil (no history to clear yet).
   * Defaults to `hasStarted` for existing call sites.
   */
  showTitle?: boolean;
  /**
   * Show the new-chat pencil (and, if `onHelp` is set, the help button to
   * its right) even when `hasStarted` is false. Used by the meal-picker
   * greeting once the user starts typing their own prompt, to match the
   * regular chat header. Defaults to `hasStarted`.
   */
  showActions?: boolean;
  /** When true, renders a left-pointing back arrow instead of the dismiss chevron. Used when the chat was entered from the help screen. */
  useBackIcon?: boolean;
  /** "white" renders all icons and title in white, for use over a dark background (meal-picker greeting). */
  variant?: "default" | "white";
}

export const ChatHeader = memo(function ChatHeader({
  hasStarted,
  onBack,
  onNewChat,
  onHelp,
  showTitle,
  showActions,
  useBackIcon,
  variant = "default",
}: ChatHeaderProps) {
  const isWhite = variant === "white";
  const titleVisible = showTitle ?? hasStarted;
  const actionsVisible = showActions ?? hasStarted;
  const setMessages = useSetAtom(chatMessagesAtom);
  const setSessionId = useSetAtom(chatSessionIdAtom);
  const setPendingImage = useSetAtom(pendingChatImageAtom);
  const setPendingContext = useSetAtom(pendingChatContextAtom);
  const setOrigin = useSetAtom(chatOriginAtom);
  const setMealPickerGreetingPending = useSetAtom(mealPickerGreetingPendingAtom);
  const [presentActionSheet] = useIonActionSheet();

  const handleNewChat = () => {
    presentActionSheet({
      header: "Start a new chat?",
      subHeader: "Your current conversation will be cleared.",
      cssClass: "new-chat-action-sheet",
      buttons: [
        {
          text: "Start a new chat",
          role: "destructive",
          handler: async () => {
            // Durable turns survive disconnects, so wait for authoritative
            // cancellation before clearing the conversation/session locally.
            const canReset = (await onNewChat?.()) ?? true;
            if (!canReset) return false;
            setMessages([]);
            setSessionId(crypto.randomUUID());
            setPendingImage([]);
            // Mirror a fresh coach open (see useOpenCoachChat): land on the
            // "what would you like to eat next?" meal-picker greeting, drop any
            // stale meal-coach context so it can't bleed into the first message,
            // mark the conversation a resumable "coach" chat, and clear a
            // still-pending post-onboarding welcome so the greeting wins.
            setPendingContext(null);
            setOrigin("coach");
            setMealPickerGreetingPending(true);
            clearChatWelcomePending();
            trackMixpanelEvent(MixpanelEvents.AI_CHAT_NEW_CHAT_STARTED);
            return true;
          },
        },
        { text: "Cancel", role: "cancel" },
      ],
    });
  };

  return (
    <div
      className={cn(
        "relative flex items-center justify-between px-2 pb-3",
        "pt-[calc(var(--safe-area-inset-top)+0.5rem)]",
      )}
    >
      <TextButton
        onClick={onBack}
        aria-label={useBackIcon ? "Back" : "Dismiss"}
        className="flex h-11 w-11 items-center justify-center"
      >
        {useBackIcon ? (
          <BackIcon className={isWhite ? "stroke-white" : "stroke-[#620ad5]"} />
        ) : (
          <ChevronDownIcon className={cn("h-11 w-11", isWhite && "stroke-white")} />
        )}
      </TextButton>

      {/*
        Absolute-centered so the title's optical position is true page-center
        regardless of which side controls render. The visible chevron weight
        otherwise pulls the perceived centre when the right slot is empty.
      */}
      {titleVisible && (
        <h1
          className={cn(
            textVariants.title3,
            "pointer-events-none text-center font-bold",
            "ml-0",
            isWhite ? "text-white" : "text-black",
          )}
        >
          NutriCoach
        </h1>
      )}

      {actionsVisible ? (
        <div className="flex items-center">
          <TextButton
            onClick={handleNewChat}
            aria-label="Start a new chat"
            className="flex h-11 w-11 items-center justify-center"
          >
            <NewChatIcon className={isWhite ? "text-white" : undefined} />
          </TextButton>
        </div>
      ) : (
        <div aria-hidden className="h-11 w-11" />
      )}
    </div>
  );
});
